import { getAccessToken } from 'redux-web/utils/session/actions'
import { isCapacitor } from './isCapacitor'

/**
 * Converts a relative URL to absolute URL using the current domain
 * @param {string} url - The URL to convert
 * @returns {string} - Absolute URL
 */
const getAbsoluteUrl = ({ token, url }) => {
  // If it's already an absolute URL, return as is
  if (url?.startsWith('http://') || url?.startsWith('https://')) {
    return url
  }

  // For relative URLs, use the web URL from environment or fallback to current origin
  let baseUrl =
    process.env.NEXT_PUBLIC_WEB_URL || process.env.NEXT_PUBLIC_API_URL

  // If no environment variable, fallback to current origin
  if (!baseUrl && typeof window !== 'undefined') {
    baseUrl = window.location.origin
  }

  // Remove trailing slash from base URL if present
  baseUrl = baseUrl?.replace(/\/$/, '')

  // Handle URLs that start with /
  if (url.startsWith('/')) {
    return token
      ? `${baseUrl}${url}?origin=capacitor&tokenFromCapacitor=${token}`
      : `${baseUrl}${url}?origin=capacitor`
  }

  // Handle URLs that don't start with /
  return token
    ? `${baseUrl}/${url}?origin=capacitor&tokenFromCapacitor=${token}`
    : `${baseUrl}/${url}?origin=capacitor`
}

/**
 * Opens a URL in the external browser when running in Capacitor
 * Falls back to window.open() on web
 * @param {string} url - The URL to open
 * @param {string} target - Target for window.open (default: '_blank')
 * @param {Function} onReturn - Callback when user returns from external browser (mobile only)
 */
//
export const openInBrowser = async (
  url,
  target = '_blank',
  onReturn = null
) => {
  const token = getAccessToken()

  // Convert relative URLs to absolute URLs
  const absoluteUrl = getAbsoluteUrl({ token, url })

  if (isCapacitor) {
    try {
      // Import Browser plugin dynamically to avoid issues on web
      const { Browser } = await import('@capacitor/browser')

      console.log('Opening URL in external browser:', absoluteUrl)

      // Set up listener for when user returns from external browser
      if (onReturn) {
        // Remove any existing listeners first
        await Browser.removeAllListeners()

        // Add listener for browser finished
        Browser.addListener('browserFinished', () => {
          console.log(
            'User returned from external browser - clearing loading state'
          )
          onReturn()
        })

        // Add listener for browser page loaded (fallback)
        Browser.addListener('browserPageLoaded', () => {
          console.log('Browser page loaded')
        })

        // Add listener for browser closed (additional fallback)
        Browser.addListener('browserClosed', () => {
          console.log('Browser closed - clearing loading state')
          onReturn()
        })
      }

      // Open URL in external browser
      await Browser.open({
        url: absoluteUrl,
        windowName: '_blank',
        presentationStyle: 'popover',
      })

      // Fallback: Clear loading state after a timeout in case events don't fire
      if (onReturn) {
        setTimeout(() => {
          console.log('Fallback: Clearing loading state after timeout')
          onReturn()
        }, 5000) // 5 second fallback
      }
    } catch (error) {
      console.error('Failed to open URL in external browser:', error)
      // Fallback to window.open
      window.open(absoluteUrl, target)
      // Call onReturn immediately for fallback
      if (onReturn) {
        onReturn()
      }
    }
  } else {
    // On web, use regular window.open
    window.open(absoluteUrl, target)
    // Call onReturn immediately for web
    if (onReturn) {
      onReturn()
    }
  }
}

/**
 * Opens a purchase page in external browser when on mobile
 * @param {string} purchaseUrl - The purchase page URL
 * @param {Function} onReturn - Callback when user returns from external browser (mobile only)
 */
export const openPurchaseInBrowser = async (purchaseUrl, onReturn = null) => {
  if (isCapacitor) {
    // On mobile, open in external browser
    await openInBrowser(purchaseUrl, '_blank', onReturn)
  } else {
    // On web, return the URL for normal navigation (don't navigate here)
    return purchaseUrl
  }
}
